"use client";

import { Danger } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { WalletAddress } from "@/components/core/WalletAddress";
import { Spinner } from "@/components/ui/spinner";

interface GuestPlacementNoticeProps {
  /** House wallet the buyer will be placed under, once resolved. */
  address: string | undefined;
  isError: boolean;
}

/**
 * Shown when the buyer unticks "I have a referrer" — i.e. elects **guest mode**.
 *
 * Guest mode is a supported way to buy, so this is not an error state. It is a
 * disclosure: the account stays a guest account (limited dashboard, no MLM
 * tree), and the placement is chosen by the system. Naming the actual wallet
 * they land under is the point — the old flow made this exact placement
 * silently, which is how buyers who *meant* to use a referrer ended up under
 * the house without ever seeing it.
 */
export const GuestPlacementNotice = ({
  address,
  isError,
}: GuestPlacementNoticeProps) => {
  const t = useTranslations("checkout");

  if (isError) {
    return (
      <p className="text-xs leading-tight text-semantic-error ms-1.5">
        {t("referrer.guestUnavailable")}
      </p>
    );
  }

  return (
    <div className="rounded-sm border border-semantic-alert/40 bg-semantic-alert/5 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <Danger
          size={16}
          variant="Bold"
          className="text-semantic-alert shrink-0 mt-0.5"
        />
        <p className="text-xs leading-relaxed text-text-primary">
          {t("referrer.guestNotice")}
        </p>
      </div>
      <div className="flex items-center gap-2 text-xs text-text-secondary">
        <span>{t("referrer.guestPlacedUnder")}</span>
        {address ? (
          <WalletAddress address={address} size={4} />
        ) : (
          <span className="flex items-center gap-1.5">
            <Spinner className="size-3.5" />
            {t("referrer.guestResolving")}
          </span>
        )}
      </div>
    </div>
  );
};
